import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export interface CheckUserSignOnResponse {
    signon: 0 | 1;
    signondate?: string | null;
    station_id?: string | number | null;
    shiftno?: number | null;
}

const toSignOnFlag = (value: unknown): 0 | 1 => {
    if (typeof value === "number") {
        return value === 1 ? 1 : 0;
    }
    if (typeof value === "boolean") {
        return value ? 1 : 0;
    }
    if (typeof value === "string") {
        const normalized = value.toLowerCase().trim();
        return normalized === "1" || normalized === "true" || normalized === "ture" ? 1 : 0;
    }
    return 0;
};

const pickFirst = (source: any, keys: string[]) => {
    for (const key of keys) {
        if (source && source[key] !== undefined && source[key] !== null) {
            return source[key];
        }
    }
    return null;
};

export const checkUserSignOnStatus = async (userName?: string): Promise<CheckUserSignOnResponse> => {
    try {
        const params = userName ? { params: { user_name: userName } } : {};
        const response: ApiResponse<any> = (await apiClient.get("/signon/check_status", params)).data;
        const payload = response?.data ?? {};

        const shiftVal = pickFirst(payload, ["shiftno", "shift_no", "shift"]);
        return {
            signon: toSignOnFlag(pickFirst(payload, ["signon", "sign_on", "is_signed_on", "signed_in", "is_signon", "isSignOn"])),
            signondate: pickFirst(payload, ["signondate", "database_date", "db_date", "sign_on_date", "date"]),
            station_id: pickFirst(payload, ["station_id", "stationid", "stationId"]),
            shiftno: shiftVal != null ? Number(shiftVal) : null,
        };
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to check user sign on status");
    }
};

export const checkUserSignOn = checkUserSignOnStatus;

export interface UserSignOnRecord {
    signon: number | null;
    signondate: string | null;
    signontime?: string | null;
    stationid: string | number | null;
    shiftno?: number | null;
    floatamt?: number | null;
}

export const signOffCurrentUser = async (userName: string): Promise<UserSignOnRecord | null> => {
    try {
        const response: ApiResponse<any> = (await apiClient.put("/signon/signoff", null, { params: { user_name: userName } })).data;
        const data = response?.data ?? null;
        if (!data) return null;
        return {
            signon: data.signon ?? null,
            signondate: data.signondate ?? null,
            signontime: data.signontime ?? null,
            stationid: data.stationid ?? null,
            shiftno: data.shiftno != null ? Number(data.shiftno) : null,
            floatamt: data.floatamt ?? null,
        };
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to sign off user");
    }
};

interface UpdateUserSignOnPayload {
    user_name: string;
    signon?: 0 | 1;
    signondate?: string;
    signontime?: string;
    station_id?: string;
    floatamt?: number;
}

const cleanParams = (params: Record<string, any>) => {
    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;
        if (typeof value === "string" && value.trim() === "") continue;
        cleaned[key] = value;
    }
    return cleaned;
};

export const getUserSignOnRecords = async (userName: string): Promise<UserSignOnRecord | null> => {
    try {
        const params: Record<string, string | number> = { user_name: userName };
        const response: ApiResponse<any> = (
            await apiClient.get("/signon/user_records", { params })
        ).data;
        const data = response?.data ?? null;
        if (!data) return null;
        return {
            signon: data.signon ?? null,
            signondate: data.signondate ?? null,
            signontime: data.signontime ?? null,
            stationid: data.stationid ?? null,
            shiftno: data.shiftno != null ? Number(data.shiftno) : null,
            floatamt: data.floatamt ?? null,
        };
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to retrieve sign-on records");
    }
};

export const updateUserSignOnRecords = async (payload: UpdateUserSignOnPayload): Promise<UserSignOnRecord | null> => {
    try {
        const params = cleanParams(payload as any);
        const response: ApiResponse<any> = (
            await apiClient.put("/signon/user_records", null, { params })
        ).data;
        const data = response?.data ?? null;
        if (!data) return null;
        return {
            signon: data.signon ?? null,
            signondate: data.signondate ?? null,
            signontime: data.signontime ?? null,
            stationid: data.stationid ?? null,
            shiftno: data.shiftno != null ? Number(data.shiftno) : null,
            floatamt: data.floatamt ?? null,
        };
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to update sign-on records");
    }
};

export interface StationOption {
    id: number;
    station_id: string;
    station_loc: string | null;
    is_occupied: boolean;
    occupied_by: string | null;
    is_assigned_elsewhere: boolean;
    next_shift?: number;
}

export interface StationListResult {
    stations: StationOption[];
    message: string;
}

export const getStations = async (terminalId?: string | null): Promise<StationListResult> => {
    try {
        const params = terminalId ? { terminal_id: terminalId } : {};
        const response: ApiResponse<any[]> = (
            await apiClient.get("/signon/stations", { params })
        ).data;
        const stations = (response?.data ?? []).map((row: any) => ({
            id: row.id,
            station_id: row.station_id,
            station_loc: row.station_loc ?? null,
            is_occupied: Boolean(row.is_occupied),
            occupied_by: row.occupied_by ?? null,
            is_assigned_elsewhere: Boolean(row.is_assigned_elsewhere),
            next_shift: row.next_shift != null ? Number(row.next_shift) : 1,
        }));
        return { stations, message: response?.message ?? "" };
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to retrieve stations");
    }
};

export const claimStation = async (stationPk: number, terminalId: string): Promise<void> => {
    try {
        await apiClient.post(`/signon/stations/${stationPk}/claim`, { terminal_id: terminalId });
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to claim station");
    }
};

export interface ServerDateTime {
    date: string;
    time: string;
    datetime: string;
}

export const getServerDateTime = async (): Promise<ServerDateTime> => {
    try {
        const response: ApiResponse<any> = (
            await apiClient.get("/signon/server_datetime")
        ).data;
        const data = response?.data ?? {};
        return {
            date: data.date ?? "",
            time: data.time ?? "",
            datetime: data.datetime ?? "",
        };
    } catch (error: any) {
        // Fallback to client-side datetime if server call fails
        const now = new Date();
        const yyyy = String(now.getFullYear());
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const dd = String(now.getDate()).padStart(2, "0");
        const hh = String(now.getHours()).padStart(2, "0");
        const min = String(now.getMinutes()).padStart(2, "0");
        const ss = String(now.getSeconds()).padStart(2, "0");
        return {
            date: `${yyyy}-${mm}-${dd}`,
            time: `${hh}:${min}:${ss}`,
            datetime: `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`,
        };
    }
};
